import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, computed, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { SelectButtonModule } from 'primeng/selectbutton';
import { ButtonModule } from 'primeng/button';
import { Subject, debounceTime, interval } from 'rxjs';
import { TranslationService } from '@/layout/service/translation.service';
import { PageHeaderComponent } from '@/shared/page-header/page-header.component';
import { EmptyStateComponent, LoadingSkeletonComponent } from '@/shared/ui';
import { MetricTileComponent } from '@/shared/ui/metric-tile/metric-tile.component';
import { MetricChartComponent } from '@/shared/metric-chart/metric-chart.component';
import { PduCardComponent } from '@/shared/power/pdu-card/pdu-card.component';
import { combinedWattSeries, currentWatts, powerSummary } from '@/shared/power/power-series';
import { POWER_SERVICE } from 'src/api/power.service.interface';
import { PowerDevice, PowerHistorySample, PowerOutlet, applyPowerUpdate, hasUnknownOutlet } from 'src/api/power';

const HISTORY_REFRESH_MS = 5 * 60_000;
/** New outlets usually appear together (a device's first status); re-read once. */
const OUTLETS_RELOAD_DEBOUNCE_MS = 1000;

/**
 * Power distribution page shared by the processor dashboard and the cloud device pages, laid out
 * like the room Power tab: KPIs (now, energy, peak, average) for every PDU together, one stacked
 * usage chart, then the PDUs in a grid of cards. The transport comes from POWER_SERVICE
 * (DevicePowerService / CloudPowerService).
 */
@Component({
    selector: 'app-power',
    standalone: true,
    imports: [FormsModule, SelectButtonModule, ButtonModule, PageHeaderComponent, EmptyStateComponent, LoadingSkeletonComponent, MetricTileComponent, MetricChartComponent, PduCardComponent],
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
        @if (showHeader()) {
            <app-page-header title="power"></app-page-header>
        }

        @if (loading()) {
            <app-loading-skeleton preset="cards" />
        } @else if (error()) {
            <app-empty-state icon="fa fa-triangle-exclamation" tone="error" [title]="t()('power.loadFailed')" [description]="error()" [actionLabel]="t()('power.retry')" actionIcon="pi pi-refresh" (action)="reload()" />
        } @else if (devices().length === 0) {
            <app-empty-state icon="fa fa-plug" [title]="t()('power.noDevices')" [description]="t()('power.noDevicesDetail')" />
        } @else {
            <div class="power-page">
                <div class="kpi-row">
                    <app-metric-tile [label]="t()('power.now')" [value]="nowWatts()" unit="W" icon="fa fa-bolt" format="1.0-0" />
                    <app-metric-tile [label]="t()('power.energyRange')" [value]="summary().energyKWh" unit="kWh" icon="fa fa-battery-half" format="1.0-2" [caption]="rangeLabel()" />
                    <app-metric-tile [label]="t()('power.peak')" [value]="summary().peakWatts" unit="W" icon="fa fa-arrow-trend-up" format="1.0-0" [caption]="rangeLabel()" />
                    <app-metric-tile [label]="t()('power.average')" [value]="summary().avgWatts" unit="W" icon="fa fa-wave-square" format="1.0-0" [caption]="rangeLabel()" />
                </div>

                <div class="card chart-card">
                    <div class="chart-header">
                        <h3 class="section-title">{{ t()('power.usageHistory') }}</h3>
                        @if (rangeOptions().length > 1) {
                            <p-selectbutton [options]="rangeOptions()" [ngModel]="hours()" (ngModelChange)="setHours($event)" optionLabel="label" optionValue="value" [allowEmpty]="false" size="small" [ariaLabel]="t()('metrics.range')" />
                        }
                    </div>
                    <app-metric-chart [series]="series()" unit="W" [stacked]="true" [windowHours]="hours()" [ariaLabel]="t()('power.usageHistory')" [emptyText]="t()('power.historyPending')" />
                </div>

                <h3 class="section-title">{{ t()('power.devicesInRoom') }}</h3>
                <div class="pdu-list">
                    @for (device of devices(); track device.key) {
                        <div class="card pdu-item">
                            <app-pdu-card [device]="device" [energyKWh]="deviceEnergy()[device.key]" (outletChanged)="mergeOutlet($event)" />
                        </div>
                    }
                </div>
            </div>
        }
    `,
    styles: [
        `
            .power-page {
                display: flex;
                flex-direction: column;
                gap: var(--app-space-4, 1rem);
                min-width: 0;
            }
            .kpi-row {
                display: grid;
                grid-template-columns: repeat(auto-fit, minmax(min(100%, 10rem), 1fr));
                gap: var(--app-space-3, 0.75rem);
            }
            .card {
                margin-bottom: 0;
            }
            .chart-header {
                display: flex;
                flex-wrap: wrap;
                align-items: center;
                justify-content: space-between;
                gap: 0.75rem;
                margin-bottom: 0.75rem;
            }
            .section-title {
                margin: 0;
                font-size: 0.9375rem;
                font-weight: 600;
            }
            .pdu-list {
                display: grid;
                grid-template-columns: repeat(auto-fill, minmax(min(100%, 30rem), 1fr));
                gap: var(--app-space-4, 1rem);
            }
            .pdu-item {
                min-width: 0;
            }
        `
    ]
})
export class PowerComponent implements OnInit {
    private power = inject(POWER_SERVICE);
    private destroyRef = inject(DestroyRef);
    readonly t = inject(TranslationService).t;

    /** Cloud mode gets its header from the Device shell, so it opts out. */
    readonly showHeader = input(true);

    readonly devices = signal<PowerDevice[]>([]);
    readonly loading = signal(true);
    readonly error = signal<string | null>(null);
    readonly hours = signal(24);
    private readonly history = signal<Record<string, PowerHistorySample[]>>({});
    private readonly outletsChanged = new Subject<void>();

    readonly rangeOptions = computed(() =>
        [
            { label: this.t()('metrics.range24h'), value: 24 },
            { label: this.t()('metrics.range7d'), value: 24 * 7 },
            { label: this.t()('metrics.range30d'), value: 24 * 30 }
        ].filter((o) => o.value <= this.power.maxHistoryHours)
    );
    readonly rangeLabel = computed(() => {
        const hours = this.hours();
        return this.t()(hours <= 24 ? 'metrics.range24h' : hours <= 24 * 7 ? 'metrics.range7d' : 'metrics.range30d');
    });

    readonly nowWatts = computed(() => currentWatts(this.devices()));
    readonly summary = computed(() => powerSummary(Object.values(this.history())));
    readonly deviceEnergy = computed(() => Object.fromEntries(this.devices().map((device) => [device.key, powerSummary([this.history()[device.key] ?? []]).energyKWh])));
    readonly series = computed(() => combinedWattSeries(this.devices(), this.history(), this.t()('power.total')));

    ngOnInit(): void {
        this.power.updates$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((update) => {
            // A device reporting an outlet the page has not seen (it learnt it from the hardware after the page loaded).
            if (hasUnknownOutlet(this.devices(), update)) this.outletsChanged.next();
            this.devices.update((list) => list.map((d) => applyPowerUpdate(d, update)));
        });
        this.outletsChanged.pipe(debounceTime(OUTLETS_RELOAD_DEBOUNCE_MS), takeUntilDestroyed(this.destroyRef)).subscribe(() => void this.reload(false));
        interval(HISTORY_REFRESH_MS)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => void this.loadHistory());
        void this.reload();
    }

    async reload(showLoading = true): Promise<void> {
        if (showLoading) this.loading.set(true);
        this.error.set(null);
        try {
            this.devices.set(await this.power.getDevices());
        } catch (e) {
            this.error.set(e instanceof Error ? e.message : String(e));
        } finally {
            this.loading.set(false);
        }
        await this.loadHistory();
    }

    setHours(hours: number): void {
        this.hours.set(hours);
        void this.loadHistory();
    }

    mergeOutlet(event: { key: string; outlet: PowerOutlet }): void {
        this.devices.update((list) => list.map((d) => applyPowerUpdate(d, { key: event.key, outlets: [event.outlet] })));
    }

    private async loadHistory(): Promise<void> {
        const hours = this.hours();
        const entries = await Promise.all(
            this.devices().map(async (d) => {
                try {
                    return [d.key, await this.power.getHistory(d, hours)] as const;
                } catch {
                    return [d.key, [] as PowerHistorySample[]] as const;
                }
            })
        );
        this.history.set(Object.fromEntries(entries));
    }
}
